import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { auth, db, storage } from "./firebase";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  setDoc,
  addDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import RosterPage from "./pages/RosterPage";
import Login from "./pages/Login";
import RostersOverviewPage from "./pages/RostersOverviewPage";
import "./styles.css";
import CalendarPage from "./components/CalendarPage";
import BottomNavBar from "./components/BottomNavBar";
import ProfilePage from "./components/ProflePage";
import Signup from "./components/SignUp";
import Loading from "./components/Loading";

function App() {
  const [user, setUser] = useState(null);
  const [people, setPeople] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        console.log("Logged-in user:", currentUser);
        setUser(currentUser);
  
        // Fetch people for the current user
        fetchAllPeople(currentUser.uid);
      } else {
        console.log("No user logged in");
        setUser(null);
        setPeople([]);
  
        // Only navigate to /login if not already on a public route
        const publicRoutes = ["/signup", "/forgot-password"]; // Add other public routes here
        if (!publicRoutes.includes(window.location.pathname)) {
          navigate("/login");
        }
      }
    });
  
    return () => unsubscribe();
  }, [navigate]);
  

  const fetchAllPeople = async (userId) => {
    if (!userId) {
      console.error("No User ID provided for fetching people.");
      return;
    }
  
    try {
      // console.log("Fetching rosters for user:", userId);
      const rostersRef = collection(db, "users", userId, "rosters");
      const rostersSnapshot = await getDocs(rostersRef);
  
      if (rostersSnapshot.empty) {
        console.log("No rosters found for this user.");
        setPeople([]);
        return;
      }
  
      let allPeople = [];
      for (const rosterDoc of rostersSnapshot.docs) {
        const rosterId = rosterDoc.id;
        // console.log(`Fetching people for roster ID: ${rosterId}`);
        const peopleRef = collection(db, "users", userId, "rosters", rosterId, "people");
        const peopleSnapshot = await getDocs(peopleRef);
  
        if (!peopleSnapshot.empty) {
          const rosterPeople = peopleSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          allPeople = [...allPeople, ...rosterPeople];
        }
      }
  
      // console.log("All fetched people:", allPeople);
      setPeople(allPeople); // Update the `people` state
    } catch (error) {
      console.error("Error fetching all people:", error);
      throw error;
    }
  };  

  const addPerson = async (rosterId, newPerson) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        console.error("No user ID found");
        return;
      }

      const personRef = await addDoc(
        collection(db, "users", userId, "rosters", rosterId, "people"),
        {
          ...newPerson,
        }
      );

      console.log("Person added with ID:", personRef.id);
    } catch (error) {
      console.error("Error adding person:", error);
    }
  };

  const deletePerson = async (rosterId, personId) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        console.error("No user ID found");
        return;
      }

      const personRef = doc(db, "users", userId, "rosters", rosterId, "people", personId);
      await deleteDoc(personRef);
      console.log("Person deleted:", personId);
    } catch (error) {
      console.error("Error deleting person:", error);
    }
  };

  const updatePerson = async (rosterId, updatedPerson) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        console.error("No user ID found");
        return;
      }

      const docRef = doc(db, "users", userId, "rosters", rosterId, "people", updatedPerson.id);

      if (updatedPerson.picture instanceof File) {
        updatedPerson.picture = await uploadImage(
          updatedPerson.picture,
          updatedPerson.id
        );
      }

      await setDoc(docRef, updatedPerson, { merge: true });

      setPeople((prevPeople) =>
        prevPeople.map((person) =>
          person.id === updatedPerson.id ? { ...updatedPerson } : person
        )
      );

      console.log("Successfully updated person:", updatedPerson);
    } catch (error) {
      console.error("Error updating person:", error);
    }
  };

  const uploadImage = async (file, documentId) => {
    if (!file) {
      console.error("No file provided for upload.");
      return null;
    }

    if (!documentId) {
      console.error("No documentId provided for upload.");
      return null;
    }

    try {
      const storagePath = `users/${auth.currentUser?.uid}/people/${documentId}/picture`;
      console.log("Uploading to path:", storagePath);

      const storageRef = ref(storage, storagePath);
      const metadata = { contentType: file.type || "application/octet-stream" };

      console.log("Starting upload...");
      const snapshot = await uploadBytes(storageRef, file, metadata);
      console.log("Upload successful:", snapshot);

      const downloadURL = await getDownloadURL(storageRef);
      console.log("File available at:", downloadURL);

      return downloadURL;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  };

  // console.log("People being passed to RosterPage:", people);


  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route
          path="/"
          element={user ? <RostersOverviewPage /> : <Loading />}
        />
        <Route
          path="/roster/:rosterId"
          element={
            user ? (
              <RosterPage
                addPerson={addPerson}
                deletePerson={deletePerson}
                updatePerson={updatePerson}
                customFields={customFields}
                setCustomFields={setCustomFields}
                people={people} // This must pass the fetched people
                setPeople={setPeople}
              />
            ) : (
              <Loading/>
            )
          }
        />

        <Route
          path="/calendar"
          element={<CalendarPage people={people || []} />}
        />
        <Route path="/profile" element={<ProfilePage />} />
      </Routes>
      {user && (
        <BottomNavBar
          photoURL={auth.currentUser?.photoURL?.replace("s96-c", "s400-c")}
        />
      )}
    </>
  );
}

export default App;
